import React from 'react'
import styles from './Gt2BuyPanel.module.css'
import {
  TorqueIcon,
  WheelIcon,
  ProfileIcon,
  RailsIcon,
  SeatIcon,
  AdjustIcon,
  ModularIcon,
  ColoursIcon,
  WrenchIcon,
  EuIcon,
  ShieldIcon,
  ReturnIcon,
} from '../../ComparisonTable/icons'

// Same one-liner the page's index.js uses; `classnames` is not a dependency here.
const cx = (...c) => c.filter(Boolean).join(' ')

// The redesigned pieces of the GT-2 buy column. Each one replaces something that
// was saying the same thing a second or third time further down the page.

// The key facts as a grid of small tiles: icon, the value, the label under it.
// ONE place for what the buy column used to say three times over (nine
// "Key Features" bullets, a four-fact trust row, the warranty / money-back / EU
// claims); a tile is scanned faster than a table row and reads as a feature.
//
// Every value comes from copy already on this page (the 45 Nm rating, the
// 160 x 40 mm 6063-T6 profile with 3.5 mm wall, 95% pre-assembly, the sliders
// and brackets "included in the price" row of the comparison table, the 3 year
// warranty on VRS manufactured components, the site-wide 30 day money back).
const SPECS = [
  { Icon: TorqueIcon, label: 'Direct drive support', value: 'Up to 45 Nm' },
  { Icon: WheelIcon, label: 'Wheelbase mount', value: 'Front or bottom' },
  { Icon: ProfileIcon, label: 'Aluminium profile', value: '160 x 40 mm' },
  { Icon: RailsIcon, label: 'Seat brackets & sliders', value: 'Included' },
  { Icon: SeatIcon, label: 'Seating positions', value: 'Rally and GT' },
  { Icon: AdjustIcon, label: 'Pedal angle, height, reach', value: 'Fully adjustable' },
  { Icon: ModularIcon, label: 'Accessory mounting', value: 'Left and right' },
  { Icon: ColoursIcon, label: 'Colour options', value: '7 colours' },
  { Icon: WrenchIcon, label: 'Assembly', value: '95% pre-assembled' },
  { Icon: EuIcon, label: 'Made in', value: 'European Union' },
  { Icon: ShieldIcon, label: 'Warranty', value: '3 years' },
  { Icon: ReturnIcon, label: 'Returns', value: '30-day money back' },
]

export function Gt2SpecStrip() {
  return (
    <section className={styles.specs} aria-label="Key features">
      <h2 className={styles.specCaption}>Key features</h2>
      <ul className={styles.specGrid}>
        {SPECS.map(({ Icon, label, value }) => (
          <li className={styles.specTile} key={label}>
            <span className={styles.specIcon}><Icon size={20}/></span>
            <span className={styles.specVal}>{value}</span>
            <span className={styles.specKey}>{label}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

// Price beside the name. The old layout had no price until the subtotal at the
// very bottom of the configurator, ~1800px down, or the sticky bar.
export function Gt2PriceLine({ price, note }) {
  if (!price) return null
  return (
    <div className={styles.priceRow}>
      <span className={styles.price}>{price}</span>
      {note && <span className={styles.priceNote}>{note}</span>}
    </div>
  )
}

export function Gt2Promise({ children }) {
  if (!children) return null
  return <p className={styles.promise}>{children}</p>
}

// A configurator step: a rounded box with a large "Step N" tab sitting on its
// top-left edge, the step name beside it and the current choice echoed on the
// right, so it is obvious at a glance where you are and what you picked. The
// box lights up while you are working inside it.
export function Gt2Group({ step, label, value, active, onActivate, children }) {
  return (
    <fieldset
      className={cx(styles.group, active && styles.groupActive)}
      onPointerDown={onActivate}
      onFocus={onActivate}
    >
      <legend className={styles.groupLegend}>
        {step != null && <span className={styles.groupStep}>Step <span className={styles.groupStepNo}>{step}</span></span>}
      </legend>
      <div className={styles.groupLabel}>
        <span className={styles.groupLabelText}>{label}</span>
        {value && <span className={styles.groupValue}>{value}</span>}
      </div>
      {children}
    </fieldset>
  )
}

// Two cards instead of a native <select>. The mount is the one choice on this
// page a buyer can get wrong, and a dropdown hides the difference behind a click.
const MOUNT_DESC = {
  front: 'Wheel base bolts to the upright plate. The usual choice.',
  bottom: 'Wheel base sits on the deck. For low mounting and formula positions.',
}

const mountDescFor = (label = '') => {
  const l = String(label).toLowerCase()
  if (l.includes('bottom')) return MOUNT_DESC.bottom
  if (l.includes('front')) return MOUNT_DESC.front
  return ''
}

export function Gt2MountCards({ options, value, onChange }) {
  return (
    <>
      <div className={styles.mountCards} role="radiogroup" aria-label="Wheelbase mount">
        {options.map((opt) => {
          const selected = opt.id === value
          return (
            <button
              type="button"
              key={opt.id}
              role="radio"
              aria-checked={selected}
              className={cx(styles.mountCard, selected && styles.mountCardSelected)}
              onClick={() => onChange(opt.id)}
            >
              <span className={styles.mountName}>{opt.label}</span>
              <span className={styles.mountDesc}>{mountDescFor(opt.label)}</span>
            </button>
          )
        })}
      </div>
      <p className={styles.mountFoot}>
        Same price either way. Your rig ships with the mount you pick here — check your wheel base in the compatibility list below.
      </p>
    </>
  )
}

// The four facts, stated ONCE, directly above the CTA where buyers actually
// look for them. Replaces Gt2TrustRow under the H1.
const TRUST = [
  'Made in the EU',
  'Ships 95% pre-assembled',
  '3-year warranty',
  '30-day money back',
]

export function Gt2TrustLine() {
  return (
    <ul className={styles.trustLine} aria-label="Why buy from VRS">
      {TRUST.map((t) => (
        <li className={styles.trustItem} key={t}>
          <span className={styles.trustDot} aria-hidden="true"/>
          {t}
        </li>
      ))}
    </ul>
  )
}
